import React, { Fragment, useEffect, useMemo, useState } from 'react';
import styles from '../index.module.css';
import { formatNumber, getKitchenById, getOrderStatus, isCSRole } from 'utils/functions';
import PickupOrder from 'components/base/pickup-order';
import FailTripModal from 'components/live-trip-cards/live-trip-modals/fail-trip-modal';
import { OrderType } from 'models/order.model';
import { EditAltIcon } from 'cokitchen-icons';
import { getCurrentTeam } from 'utils/role-config';
import CopyText from 'components/base/copy-text';
import useRoleContext from 'contexts/role.context';
import {
  calculateDeliveryTime,
  getDeliveryType,
  getOrderDateTime,
  getValidDeliveryDate
} from '../util';
import ChangeAddressModal from 'components/live-trip-cards/live-trip-modals/change-address-modal';
import { ENUM_COKITCHEN_TEAM } from '@cokitchen/cokitchen-auth';
import moment from 'moment';

type Props = {
  order: OrderType;
  show: boolean;
  addressModal: (withRes?: boolean) => void;
};

const heaviside = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371e3; // metres
  const φ1 = (lat1 * Math.PI) / 180; // φ, λ in radians
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const d = R * c; // in metres
  return d;
};

const OrderDetailsDelivery = ({ order, addressModal, show }: Props) => {
  const [distance, setDistance] = useState('0.000');

  const [latLng, setLatLng] = useState({
    lat: '',
    lng: ''
  });
  const [showFailTripModal, setShowFailTripModal] = useState(false);
  const orderStatus = useMemo(() => getOrderStatus(order), [order]);
  const { role } = useRoleContext();
  const [timeDifference, setTimeDifference] = useState({
    text: '',
    value: 0
  });

  let duration = 'In progress';
  if (!order.calculated_order.pickup) {
    if (order.completed) {
      const start_time = new Date(order.kitchen_dispatched_time ?? 0);
      const end_time = new Date(order.completed_time ?? 0);
      const duration_in_millsec = Number(end_time) - Number(start_time);
      const duration_in_min = Math.floor(duration_in_millsec / 1000 / 60);
      duration = `${duration_in_min} mins`;
    }
  }

  const calculateDistance = async () => {
    if (order.calculated_order.pickup) return;
    const kitchen = await getKitchenById(order.calculated_order.cokitchen_id);
    const distance = heaviside(
      Number(order.calculated_order.lat),
      Number(order.calculated_order.lng),
      Number(kitchen.lat),
      Number(kitchen.lng)
    );
    setDistance((distance / 1000).toFixed(3));
  };

  const getLatLng = async () => {
    const kitchen = await getKitchenById(order?.calculated_order?.cokitchen_id);
    const { lat, lng } = kitchen;
    setLatLng({
      lat,
      lng
    });
  };

  const addressLine = `https://www.google.com/maps/dir/?api=1&origin=${latLng?.lat},${latLng?.lng}&destination=${order?.calculated_order?.lat},${order?.calculated_order?.lng}&travelmode=driving`;

  const updateTimeDifference = ({ time, text }: { time: string; text: string }) => {
    const td = new Date(time).getTime() - new Date().getTime();
    const value = Math.floor((td / 1000 / 60) % 60);
    setTimeDifference({ text, value });
  };

  useEffect(() => {
    if (!order.calculated_order) return;
    calculateDistance();
    getLatLng();
  }, []);

  useEffect(() => {
    if (!role || order.pickup) return;

    const diffTime = calculateDeliveryTime({ order, orderStatus, role });
    updateTimeDifference(diffTime);

    const interval = setInterval(() => calculateDeliveryTime({ order, orderStatus, role }), 60000);
    return () => clearInterval(interval);
  }, [order, orderStatus, role]);

  const deliveryType = getDeliveryType(order);

  const preOrderDeliveryDate = getValidDeliveryDate(
    order?.preorder_delivery_time,
    order?.preorder_delivery_datetime
  );
  const scheduledOrderDeliveryDate = getValidDeliveryDate(
    order?.scheduled_delivery_time,
    order?.scheduled_delivery_datetime
  );
  const orderDateTime = getOrderDateTime(order);

  return (
    <div className={`${styles['section']} text-gray-5`}>
      {order.calculated_order.pickup ? (
        <PickupOrder />
      ) : (
        <Fragment>
          <div className='bg-white py-[18px] flex-col gap-2 md:flex-row justify-between rounded-[6px]'>
            <div className='flex justify-between items-center w-full'>
              <p className='font-medium flex gap-5'>
                Delivery Details
                {deliveryType && (
                  <span className='bg-gray-border/30 border rounded-2xl text-xs inline-flex items-center px-1.5'>
                    {deliveryType}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className='w-full bg-[#e2e7ee] h-[0.7px]'></div>
          <div className='flex flex-col gap-4 pt-6 pb-3'>
            <div className='flex justify-between gap-24 items-start w-full'>
              <p> Address </p>
              <div className='flex gap-2 items-start'>
                <a target='_blank' rel='noreferrer' href={addressLine} className='text-right'>
                  {order.calculated_order.address_details.address_line}
                </a>
                {getCurrentTeam() !== ENUM_COKITCHEN_TEAM.KITCHEN && (
                  <span
                    className='bg-gray-border rounded-lg p-1'
                    role='button'
                    onClick={() => addressModal()}>
                    <EditAltIcon className='cursor-pointer w-4 h-4' />
                  </span>
                )}
              </div>
            </div>
            <div className='flex justify-between items-center py-[5px] flex-nowrap w-full'>
              <p> Contact number: </p>
              {order.calculated_order.address_details.phone_number ? (
                <CopyText text={order.calculated_order.address_details.phone_number} />
              ) : (
                <span>-</span>
              )}
            </div>
            {timeDifference.text ? (
              <div className='flex justify-between w-full py-[5px] items-center'>
                <p>{timeDifference.text}:</p>
                <span
                  className={`inline ${
                    order.kitchen_time_elapsed
                      ? 'text-[#46AE31] bg-[#EEF9EB]'
                      : 'text-[#E60A2B] bg-[#FAEFF1]'
                  }  rounded-full text-xs  py-2 px-[7px] ml-2`}>
                  <span>{timeDifference.value ? `${timeDifference.value} mins` : '--'}</span>
                </span>
              </div>
            ) : null}

            <div className='flex py-[5px] items-center'>
              <div className='flex justify-between items-center  w-full'>
                <p> Delivery fee: </p>
                <p>{formatNumber(order.calculated_order.delivery_fee)}</p>
              </div>
            </div>
            <div className='flex py-[5px] items-center'>
              <div className='flex justify-between items-center  w-full'>
                <p> Payment method: </p>
                <p>{order?.order_details?.bank_transfer ? 'Transfer' : 'Card'}</p>
              </div>
            </div>

            <div className='flex justify-between tems-center py-[5px] w-full'>
              <p> Amount paid by wallet: </p>
              <p>{formatNumber(order.order_details.wallet_amount ?? 0)} </p>
            </div>

            <div className='flex justify-between w-full py-[5px] items-center'>
              <p> Total trip time:</p>
              <p>{duration}</p>
            </div>
            {(preOrderDeliveryDate || scheduledOrderDeliveryDate) && (
              <div className='flex justify-between w-full py-[5px] items-center'>
                <p> Order delivery date:</p>
                <p>
                  {moment(preOrderDeliveryDate ?? scheduledOrderDeliveryDate).format('DD/MM/YYYY')}
                </p>
              </div>
            )}

            {Number(orderDateTime?.length) > 0 && (
              <div className='flex justify-between w-full py-[5px] items-center'>
                <p> Order delivery time:</p>
                <p>
                  {Array.isArray(orderDateTime)
                    ? `${moment(orderDateTime?.[0], 'HH:mm').format('hh:mm A')} - ${moment(
                        orderDateTime?.[1],
                        'HH:mm'
                      ).format('hh:mm A')}`
                    : moment(orderDateTime).format('hh:mm A')}
                </p>
              </div>
            )}

            <div className='flex justify-between w-full pt-[5px] pb-8 items-center'>
              <p>Total trip distance:</p>
              <p>{distance} km</p>
            </div>

            {order.order_details.rider_note && (
              <div className='mb-6'>
                <div className='text-sm p-3 bg-gray-1'>
                  <span className='font-semibold'>NOTE:</span>
                  {order.order_details.rider_note}
                </div>
              </div>
            )}
            {!order.completed && !isCSRole() && (
              <div>
                <button
                  className='border border-[#E3E6E8] text-center  w-full rounded-[4px] py-3 text-gray-6 font-medium text-sm'
                  onClick={() => setShowFailTripModal(!showFailTripModal)}>
                  Fail trip
                </button>
              </div>
            )}
          </div>
        </Fragment>
      )}
      <FailTripModal onClose={() => setShowFailTripModal(false)} show={showFailTripModal} />
      <ChangeAddressModal show={show} onClose={addressModal} order={order} />
    </div>
  );
};

export default OrderDetailsDelivery;
